import React from 'react';
import PropTypes from 'prop-types';
import { datadogRum } from '@datadog/browser-rum';
import {
  Box, Button, ButtonVariant, Text,
} from '@usealloy/component-library';

import log from '~/helpers/logging';
import { attemptStaleChunkReload, isStaleChunkError } from '~/helpers/lazyWithRetry';


const ErrorBoundaryPropTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  errorChildren: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.bool,
  ]),
};

const ErrorBoundaryDefaultProps = {
  errorChildren: false,
};

class ErrorBoundary extends React.PureComponent {
  static getDerivedStateFromError(error) {
    return { hasError: true, staleChunk: isStaleChunkError(error) };
  }

  constructor(props) {
    super(props);
    this.state = { hasError: false, staleChunk: false };
  }

  componentDidCatch(error, errorInfo) {
    // A stale chunk means the user is on an older bundle than the one deployed,
    // not that the page is broken. Tagged separately so the two are countable
    // apart in Datadog. See FRONTEND-690.
    const staleChunk = isStaleChunkError(error);
    const reloaded = staleChunk && attemptStaleChunkReload(window.location);

    let errorScreen = 'Something went wrong';
    if (staleChunk) {
      errorScreen = reloaded ? 'Stale chunk auto-reload' : 'New version available';
    }

    const context = {
      error: { ...errorInfo },
      originatedUrl: window.location.pathname,
      url: window.location.pathname,
      pageCrashed: true,
      errorScreen,
      staleChunk,
    };

    // Extract component name from errorInfo if available
    if (errorInfo && errorInfo.componentStack) {
      // Look for component names in the stack trace
      // Common patterns: "in ComponentName", "at ComponentName", "ComponentName.render"
      const componentPatterns = [
        /in\s+([A-Z][a-zA-Z0-9]*)/, // "in ComponentName"
        /at\s+([A-Z][a-zA-Z0-9]*)/, // "at ComponentName"
        /([A-Z][a-zA-Z0-9]*)\.render/, // "ComponentName.render"
        /([A-Z][a-zA-Z0-9]*)\s+\(/, // "ComponentName ("
      ];

      for (const pattern of componentPatterns) {
        const [, componentName] = errorInfo.componentStack.match(pattern) || [];
        if (componentName) {
          context.errorComponent = componentName;
          break;
        }
      }
    }

    // Send error to Datadog so we can monitor page crashes
    datadogRum.addError(error, context);
    log('error', error.message, context, error);
  }

  render() {
    const { children, errorChildren } = this.props;
    const { hasError, staleChunk } = this.state;

    if (hasError) {
      if (errorChildren) return errorChildren;

      // The page the user asked for never loaded because their tab is running an
      // older bundle. A reload was already attempted (see componentDidCatch); if
      // it happened just now this screen is transient, and if it already
      // happened this session, this is the actionable fallback.
      if (staleChunk) {
        return (
          <Box direction="column" alignItems="flex-start" gap={2} style={{ margin: 'var(--cl-space-6)' }}>
            <Text as="h1" size={5} weight={500}>A new version is available</Text>
            <Text as="h2" size={4} weight={400}>Reload the page to continue.</Text>
            <Button
              onClick={() => { window.location.reload(); }}
              variant={ButtonVariant.primary}
            >
              Reload page
            </Button>
          </Box>
        );
      }

      return (
        <Box direction="column" alignItems="flex-start" gap={2} style={{ margin: 'var(--cl-space-6)' }}>
          <Text as="h1" size={5} weight={500}>Something went wrong</Text>
          <Text as="h2" size={4} weight={400}>{'This page isn\'t working right now. Please try again or contact support.'}</Text>
        </Box>
      );
    }

    return children;
  }
}
ErrorBoundary.propTypes = ErrorBoundaryPropTypes;
ErrorBoundary.defaultProps = ErrorBoundaryDefaultProps;

export default ErrorBoundary;
